import React, { useEffect, useState, useRef } from "react";
import { Loader2, Plus, Trash2, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  "golden-triangle",
  "rajasthan",
  "spiritual",
  "wildlife",
  "himalayan",
  "south-india",
  "festivals",
  "luxury",
  "honeymoon",
  "family",
  "extension",
];

const DIFFICULTIES = ["Easy", "Moderate", "Challenging"];

const OTA_PLATFORMS = [
  { key: "getyourguide", label: "GetYourGuide" },
  { key: "viator", label: "Viator" },
  { key: "klook", label: "Klook" },
  { key: "tourradar", label: "TourRadar" },
  { key: "expedia", label: "Expedia" },
  { key: "civitatis", label: "Civitatis" },
];

const EMPTY_TOUR = {
  slug: "",
  title: "",
  category: "golden-triangle",
  summary: "",
  description: "",
  duration: "",
  days: 0,
  nights: 0,
  price_from: 0,
  price_usd: 0,
  max_group_size: 12,
  difficulty_level: "Easy",
  country: "India",
  region: "",
  image_url: "",
  gallery: [],
  highlights: [],
  itinerary: [],
  inclusions: [],
  exclusions: [],
  locations: [],
  sketch_map_coords: [],
  featured: false,
  themes: [],
  ota_prices: {},
};

// Helpers: list textarea (one item per line) <-> array
const linesToArray = (s) =>
  s
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
const arrayToLines = (a) => (Array.isArray(a) ? a.join("\n") : "");

// Section wrapper
function Section({ title, children }) {
  return (
    <div className="border border-cream-200 bg-white p-5 rounded-sm">
      <h3 className="font-serif text-lg mb-4 pb-3 border-b border-cream-200 text-deepred">
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, htmlFor, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.15em] text-foreground/70">
        {label}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-foreground/50">{hint}</p>}
    </div>
  );
}

export default function TourEditor({ open, mode, slug, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(EMPTY_TOUR);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const mainFileRef = useRef(null);
  const galleryFileRef = useRef(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  // Convert a backend-relative file URL into a full URL for <img>
  const fullUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

  // Mirror string-list fields locally as multiline text for smoother editing
  const [textLists, setTextLists] = useState({
    highlights: "",
    inclusions: "",
    exclusions: "",
    locations: "",
    gallery: "",
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && slug) {
      setLoading(true);
      api
        .get(`/tours/${slug}`)
        .then((res) => {
          const t = { ...EMPTY_TOUR, ...res.data };
          setForm(t);
          setTextLists({
            highlights: arrayToLines(t.highlights),
            inclusions: arrayToLines(t.inclusions),
            exclusions: arrayToLines(t.exclusions),
            locations: arrayToLines(t.locations),
            gallery: arrayToLines(t.gallery),
          });
        })
        .catch((e) => {
          toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load tour");
          onClose();
        })
        .finally(() => setLoading(false));
    } else {
      setForm(EMPTY_TOUR);
      setTextLists({ highlights: "", inclusions: "", exclusions: "", locations: "", gallery: "" });
    }
  }, [open, isEdit, slug, onClose]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Image upload helpers
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.url; // e.g. "/api/files/xxx/yyy.png"
  };

  const handleMainUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMain(true);
    try {
      const url = await uploadFile(file);
      setField("image_url", url);
      toast.success("Main image uploaded");
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingMain(false);
      if (mainFileRef.current) mainFileRef.current.value = "";
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingGallery(true);
    const uploadedUrls = [];
    try {
      for (const f of files) {
        const url = await uploadFile(f);
        uploadedUrls.push(url);
      }
      // Append to existing gallery textarea
      setTextLists((s) => ({
        ...s,
        gallery: (s.gallery ? s.gallery + "\n" : "") + uploadedUrls.join("\n"),
      }));
      toast.success(`Uploaded ${uploadedUrls.length} image${uploadedUrls.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingGallery(false);
      if (galleryFileRef.current) galleryFileRef.current.value = "";
    }
  };

  // Itinerary handlers
  const addItineraryDay = () =>
    setField("itinerary", [
      ...form.itinerary,
      { day: form.itinerary.length + 1, title: "", description: "" },
    ]);
  const updateItinerary = (idx, key, val) =>
    setField(
      "itinerary",
      form.itinerary.map((d, i) => (i === idx ? { ...d, [key]: val } : d))
    );
  const removeItinerary = (idx) =>
    setField(
      "itinerary",
      form.itinerary.filter((_, i) => i !== idx)
    );

  // Sketch map coords handlers
  const addCoord = () =>
    setField("sketch_map_coords", [...form.sketch_map_coords, { lat: 0, lng: 0, label: "" }]);
  const updateCoord = (idx, key, val) =>
    setField(
      "sketch_map_coords",
      form.sketch_map_coords.map((c, i) => (i === idx ? { ...c, [key]: val } : c))
    );
  const removeCoord = (idx) =>
    setField(
      "sketch_map_coords",
      form.sketch_map_coords.filter((_, i) => i !== idx)
    );

  const handleSave = async () => {
    if (!form.slug.trim() || !form.title.trim() || !form.image_url.trim()) {
      toast.error("Slug, Title and Image URL are required");
      return;
    }

    const payload = {
      ...form,
      days: Number(form.days) || 0,
      nights: Number(form.nights) || 0,
      price_from: Number(form.price_from) || 0,
      price_usd: Number(form.price_usd) || 0,
      max_group_size: Number(form.max_group_size) || 0,
      highlights: linesToArray(textLists.highlights),
      inclusions: linesToArray(textLists.inclusions),
      exclusions: linesToArray(textLists.exclusions),
      locations: linesToArray(textLists.locations),
      gallery: linesToArray(textLists.gallery),
      itinerary: form.itinerary.map((d, i) => ({
        day: Number(d.day) || i + 1,
        title: d.title || "",
        description: d.description || "",
      })),
      sketch_map_coords: form.sketch_map_coords.map((c) => ({
        lat: Number(c.lat) || 0,
        lng: Number(c.lng) || 0,
        label: c.label || "",
      })),
      ota_prices: Object.fromEntries(
        OTA_PLATFORMS
          .map(({ key }) => [key, Number(form.ota_prices?.[key]) || 0])
          .filter(([_, v]) => v > 0)
      ),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/tours/${slug}`, payload);
        toast.success("Tour updated");
      } else {
        await api.post(`/admin/tours`, payload);
        toast.success("Tour created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto bg-cream-100 p-0"
        data-testid="tour-editor-sheet"
      >
        <SheetHeader className="sticky top-0 z-10 bg-white border-b border-cream-200 px-6 py-4">
          <SheetTitle className="font-serif text-2xl text-foreground">
            {isEdit ? "Edit tour" : "Create new tour"}
          </SheetTitle>
          <SheetDescription className="text-xs uppercase tracking-[0.2em] text-saffron">
            {isEdit ? slug : "New entry"}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="p-16 text-center">
            <Loader2 className="w-6 h-6 animate-spin inline text-saffron" />
          </div>
        ) : (
          <div className="p-6 space-y-6 pb-32">
            {/* BASICS */}
            <Section title="Basics">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Slug *" htmlFor="f-slug" hint="URL identifier — lowercase, dashes only">
                  <Input
                    id="f-slug"
                    value={form.slug}
                    onChange={(e) => setField("slug", e.target.value)}
                    placeholder="golden-triangle-classic"
                    data-testid="editor-slug"
                  />
                </Field>
                <Field label="Title *" htmlFor="f-title">
                  <Input
                    id="f-title"
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="Golden Triangle Classic"
                    data-testid="editor-title"
                  />
                </Field>
              </div>

              <Field label="Summary" htmlFor="f-summary" hint="One-line teaser on tour cards">
                <Input
                  id="f-summary"
                  value={form.summary}
                  onChange={(e) => setField("summary", e.target.value)}
                  data-testid="editor-summary"
                />
              </Field>

              <Field label="Description" htmlFor="f-desc" hint="Full overview shown on tour detail page">
                <Textarea
                  id="f-desc"
                  rows={4}
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  data-testid="editor-description"
                />
              </Field>

              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Category" htmlFor="f-cat">
                  <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                    <SelectTrigger id="f-cat" data-testid="editor-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Country" htmlFor="f-country">
                  <Input
                    id="f-country"
                    value={form.country}
                    onChange={(e) => setField("country", e.target.value)}
                    data-testid="editor-country"
                  />
                </Field>
                <Field label="Region" htmlFor="f-region">
                  <Input
                    id="f-region"
                    value={form.region}
                    onChange={(e) => setField("region", e.target.value)}
                    placeholder="Delhi · Agra · Jaipur"
                    data-testid="editor-region"
                  />
                </Field>
              </div>

              <Field label="Main image URL *" htmlFor="f-img" hint="Hero photo for the tour card and detail header. Paste a URL or upload below.">
                <Input
                  id="f-img"
                  value={form.image_url}
                  onChange={(e) => setField("image_url", e.target.value)}
                  placeholder="https://..."
                  data-testid="editor-image-url"
                />
                <div className="flex items-center gap-3 mt-2">
                  <input
                    ref={mainFileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleMainUpload}
                    className="hidden"
                    data-testid="editor-image-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => mainFileRef.current?.click()}
                    disabled={uploadingMain}
                    data-testid="editor-image-upload-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider bg-peacock hover:bg-deepred text-white rounded-sm transition-colors disabled:opacity-50"
                  >
                    {uploadingMain ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-3.5 h-3.5" /> Upload image
                      </>
                    )}
                  </button>
                  {form.image_url && (
                    <img
                      src={fullUrl(form.image_url)}
                      alt="Preview"
                      className="h-10 w-16 object-cover rounded-sm border border-cream-200"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                </div>
              </Field>

              <div className="flex items-center gap-3 pt-2">
                <Switch
                  id="f-featured"
                  checked={!!form.featured}
                  onCheckedChange={(v) => setField("featured", v)}
                  data-testid="editor-featured"
                />
                <Label htmlFor="f-featured" className="text-sm cursor-pointer">
                  Show this tour as <strong>Featured</strong> on the homepage
                </Label>
              </div>

              <Field label="Themes" hint="Tag this tour so it appears on themed landing pages (/themes/yoga, /themes/wildlife, /themes/festivals)">
                <div className="flex flex-wrap gap-3 pt-1" data-testid="editor-themes">
                  {["yoga", "wildlife", "festivals"].map((t) => {
                    const active = Array.isArray(form.themes) && form.themes.includes(t);
                    return (
                      <label
                        key={t}
                        className={`inline-flex items-center gap-2 px-3.5 py-2 border rounded-sm cursor-pointer text-sm transition-colors ${
                          active ? "bg-saffron text-white border-saffron" : "bg-white border-cream-200 hover:border-saffron"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={active}
                          onChange={() => {
                            const cur = Array.isArray(form.themes) ? form.themes : [];
                            setField(
                              "themes",
                              active ? cur.filter((x) => x !== t) : [...cur, t]
                            );
                          }}
                          data-testid={`editor-theme-${t}`}
                        />
                        <span className="capitalize">{t}</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
            </Section>

            {/* PRICING & LOGISTICS */}
            <Section title="Pricing & logistics">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Price from (INR) ₹" htmlFor="f-pinr">
                  <Input
                    id="f-pinr"
                    type="number"
                    min="0"
                    value={form.price_from}
                    onChange={(e) => setField("price_from", e.target.value)}
                    data-testid="editor-price-inr"
                  />
                </Field>
                <Field label="Price from (USD) $" htmlFor="f-pusd">
                  <Input
                    id="f-pusd"
                    type="number"
                    min="0"
                    value={form.price_usd}
                    onChange={(e) => setField("price_usd", e.target.value)}
                    data-testid="editor-price-usd"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Duration label" htmlFor="f-dur" hint="Display text e.g. '6 Days · 5 Nights'">
                  <Input
                    id="f-dur"
                    value={form.duration}
                    onChange={(e) => setField("duration", e.target.value)}
                    data-testid="editor-duration"
                  />
                </Field>
                <Field label="Days" htmlFor="f-days">
                  <Input
                    id="f-days"
                    type="number"
                    min="0"
                    value={form.days}
                    onChange={(e) => setField("days", e.target.value)}
                    data-testid="editor-days"
                  />
                </Field>
                <Field label="Nights" htmlFor="f-nights">
                  <Input
                    id="f-nights"
                    type="number"
                    min="0"
                    value={form.nights}
                    onChange={(e) => setField("nights", e.target.value)}
                    data-testid="editor-nights"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Max group size" htmlFor="f-grp">
                  <Input
                    id="f-grp"
                    type="number"
                    min="1"
                    value={form.max_group_size}
                    onChange={(e) => setField("max_group_size", e.target.value)}
                    data-testid="editor-group-size"
                  />
                </Field>
                <Field label="Difficulty" htmlFor="f-diff">
                  <Select
                    value={form.difficulty_level}
                    onValueChange={(v) => setField("difficulty_level", v)}
                  >
                    <SelectTrigger id="f-diff" data-testid="editor-difficulty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Section>

            {/* CONTENT LISTS */}
            <Section title="Highlights, inclusions, exclusions & locations">
              <Field label="Highlights (one per line)" htmlFor="f-hi">
                <Textarea
                  id="f-hi"
                  rows={4}
                  value={textLists.highlights}
                  onChange={(e) => setTextLists((s) => ({ ...s, highlights: e.target.value }))}
                  placeholder={"Sunrise at the Taj Mahal\nElephant ride at Amber Fort\nBlock printing workshop"}
                  data-testid="editor-highlights"
                />
              </Field>
              <Field label="Inclusions (one per line)" htmlFor="f-inc">
                <Textarea
                  id="f-inc"
                  rows={4}
                  value={textLists.inclusions}
                  onChange={(e) => setTextLists((s) => ({ ...s, inclusions: e.target.value }))}
                  placeholder={"5 nights heritage hotel accommodation\nAll private transfers\nEnglish-speaking guide"}
                  data-testid="editor-inclusions"
                />
              </Field>
              <Field label="Exclusions (one per line)" htmlFor="f-exc">
                <Textarea
                  id="f-exc"
                  rows={3}
                  value={textLists.exclusions}
                  onChange={(e) => setTextLists((s) => ({ ...s, exclusions: e.target.value }))}
                  data-testid="editor-exclusions"
                />
              </Field>
              <Field label="Locations (one per line)" htmlFor="f-loc">
                <Textarea
                  id="f-loc"
                  rows={3}
                  value={textLists.locations}
                  onChange={(e) => setTextLists((s) => ({ ...s, locations: e.target.value }))}
                  placeholder={"Delhi\nAgra\nJaipur"}
                  data-testid="editor-locations"
                />
              </Field>
              <Field label="Gallery image URLs (one per line)" htmlFor="f-gal">
                <Textarea
                  id="f-gal"
                  rows={3}
                  value={textLists.gallery}
                  onChange={(e) => setTextLists((s) => ({ ...s, gallery: e.target.value }))}
                  data-testid="editor-gallery"
                />
                <div className="flex items-center gap-3 mt-2">
                  <input
                    ref={galleryFileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    onChange={handleGalleryUpload}
                    className="hidden"
                    data-testid="editor-gallery-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => galleryFileRef.current?.click()}
                    disabled={uploadingGallery}
                    data-testid="editor-gallery-upload-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider bg-peacock hover:bg-deepred text-white rounded-sm transition-colors disabled:opacity-50"
                  >
                    {uploadingGallery ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-3.5 h-3.5" /> Upload gallery images
                      </>
                    )}
                  </button>
                  <span className="text-[11px] text-foreground/55">Pick multiple files at once</span>
                </div>
              </Field>
            </Section>

            {/* ITINERARY */}
            <Section title="Day-by-day itinerary">
              {form.itinerary.length === 0 && (
                <p className="text-sm text-foreground/60 italic">No days added yet.</p>
              )}
              {form.itinerary.map((d, idx) => (
                <div
                  key={idx}
                  className="border border-cream-200 bg-cream-50 p-4 rounded-sm space-y-3 relative"
                  data-testid={`editor-itinerary-day-${idx}`}
                >
                  <button
                    type="button"
                    onClick={() => removeItinerary(idx)}
                    className="absolute top-3 right-3 text-foreground/40 hover:text-destructive"
                    aria-label="Remove day"
                    data-testid={`editor-remove-day-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="grid grid-cols-[80px_1fr] gap-3">
                    <Field label="Day #" htmlFor={`f-day-${idx}`}>
                      <Input
                        id={`f-day-${idx}`}
                        type="number"
                        min="1"
                        value={d.day}
                        onChange={(e) => updateItinerary(idx, "day", e.target.value)}
                      />
                    </Field>
                    <Field label="Title" htmlFor={`f-day-title-${idx}`}>
                      <Input
                        id={`f-day-title-${idx}`}
                        value={d.title}
                        onChange={(e) => updateItinerary(idx, "title", e.target.value)}
                        placeholder="Arrival in Delhi"
                      />
                    </Field>
                  </div>
                  <Field label="Description" htmlFor={`f-day-desc-${idx}`}>
                    <Textarea
                      id={`f-day-desc-${idx}`}
                      rows={3}
                      value={d.description}
                      onChange={(e) => updateItinerary(idx, "description", e.target.value)}
                    />
                  </Field>
                </div>
              ))}
              <button
                type="button"
                onClick={addItineraryDay}
                className="inline-flex items-center gap-2 text-sm text-saffron hover:text-deepred transition-colors"
                data-testid="editor-add-day"
              >
                <Plus className="w-4 h-4" /> Add day
              </button>
            </Section>

            {/* SKETCH MAP COORDS */}
            <Section title="Sketch map markers (Leaflet)">
              {form.sketch_map_coords.length === 0 && (
                <p className="text-sm text-foreground/60 italic">No markers added yet.</p>
              )}
              {form.sketch_map_coords.map((c, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end"
                  data-testid={`editor-coord-${idx}`}
                >
                  <Field label="Lat" htmlFor={`f-lat-${idx}`}>
                    <Input
                      id={`f-lat-${idx}`}
                      type="number"
                      step="0.0001"
                      value={c.lat}
                      onChange={(e) => updateCoord(idx, "lat", e.target.value)}
                    />
                  </Field>
                  <Field label="Lng" htmlFor={`f-lng-${idx}`}>
                    <Input
                      id={`f-lng-${idx}`}
                      type="number"
                      step="0.0001"
                      value={c.lng}
                      onChange={(e) => updateCoord(idx, "lng", e.target.value)}
                    />
                  </Field>
                  <Field label="Label" htmlFor={`f-lbl-${idx}`}>
                    <Input
                      id={`f-lbl-${idx}`}
                      value={c.label}
                      onChange={(e) => updateCoord(idx, "label", e.target.value)}
                      placeholder="Delhi"
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={() => removeCoord(idx)}
                    className="mb-2 text-foreground/40 hover:text-destructive"
                    aria-label="Remove marker"
                    data-testid={`editor-remove-coord-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addCoord}
                className="inline-flex items-center gap-2 text-sm text-saffron hover:text-deepred transition-colors"
                data-testid="editor-add-coord"
              >
                <Plus className="w-4 h-4" /> Add marker
              </button>
            </Section>

            {/* OTA PRICES — for the price-comparison widget shown on tour detail */}
            <Section title="OTA reference prices (USD) — for price-comparison widget">
              <p className="text-xs text-foreground/65 -mt-2 mb-3">
                Enter the price this same tour is listed at on each OTA. The customer-facing tour page will display
                a &ldquo;Best price guaranteed — save $X vs Viator&rdquo; widget, driving direct bookings.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {OTA_PLATFORMS.map(({ key, label }) => (
                  <div key={key}>
                    <Label htmlFor={`f-ota-${key}`} className="text-xs uppercase tracking-[0.15em] text-foreground/70">
                      {label}
                    </Label>
                    <Input
                      id={`f-ota-${key}`}
                      type="number"
                      min="0"
                      step="1"
                      value={form.ota_prices?.[key] || ""}
                      onChange={(e) =>
                        setField("ota_prices", {
                          ...(form.ota_prices || {}),
                          [key]: e.target.value,
                        })
                      }
                      placeholder="—"
                      data-testid={`editor-ota-price-${key}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-foreground/55 mt-2">
                Tip: set OTA prices ~25% higher than your direct USD price (e.g. direct $458 → Viator $570) to reflect
                their commission. Leave blank to hide that row in the widget.
              </p>
            </Section>
          </div>
        )}

        {/* FOOTER ACTIONS */}
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-cream-200 px-6 py-4 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-cream-300 rounded-sm text-sm hover:bg-cream-100 disabled:opacity-50"
            data-testid="editor-cancel"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-saffron hover:bg-deepred text-white rounded-sm text-sm font-medium uppercase tracking-wider transition-colors disabled:opacity-50"
            data-testid="editor-save"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              <>{isEdit ? "Save changes" : "Create tour"}</>
            )}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
